import React, { useEffect, useState } from 'react';
import ErrorMessage from '../../components/ErrorMessage';
import trainingInstanceService from '../../services/trainingInstanceService';
import { logger } from '../../utils/logger';
import {
  AssessmentTaker,
  AssessmentTakerResults,
  getAssessmentById,
  isQuestionAnswered,
  type AssessmentState,
} from '../assessment';
import LoadingSection from './LoadingSection';
import Section from './Section';
import { addAssessmentTaskResponseId, updateCurrentStepInCourse, useCompleteStep, useSteps } from './StepsContext';
import { CompletedView, InProgressView, NotStartedView } from './components/AssessmentStepViews';
import { TabbedPanel } from './components/TabbedPanel';

type LoadState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; assessment: AssessmentState };

type ViewMode =
  | { type: 'notStarted' }
  | { type: 'inProgress' }
  | { type: 'taking'; responseId: string }
  | { type: 'completed' };

type InitialViewKind = 'notStarted' | 'inProgress' | 'completed';

function countAnsweredQuestions(assessment: AssessmentState): number {
  return assessment.questions.filter(isQuestionAnswered).length;
}

function isFullyAnswered(assessment: AssessmentState): boolean {
  return countAnsweredQuestions(assessment) >= assessment.questions.length;
}

function deriveInitialViewMode(assessment: AssessmentState, stepStatus: string): InitialViewKind {
  if (stepStatus === 'not_started') return 'notStarted';
  if (isFullyAnswered(assessment)) return 'completed';
  return 'inProgress';
}

export default function V2AssessmentStep() {
  const { completeStep } = useCompleteStep();
  const [{ currentCourse, currentStep }, dispatch] = useSteps();
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [viewMode, setViewMode] = useState<ViewMode>({ type: 'notStarted' });
  const [isStarting, setIsStarting] = useState(false);

  const setAssessment = (assessment: AssessmentState) => setLoadState({ status: 'ready', assessment });

  const handleError = (error: Error) => {
    logger.error(error);
    setLoadState({ status: 'error' });
  };

  const updateViewMode = (vm: ViewMode) => {
    if (vm.type !== 'taking') return setViewMode(vm);
    if (!vm.responseId) {
      logger.error('Attempted to enter AssessmentTaker without a responseId');
      return;
    }
    return setViewMode(vm);
  };

  const handleStart = (): void => {
    if (currentStep.status !== 'not_started') {
      updateViewMode({ type: 'taking', responseId: currentStep.task.responseId });
      return;
    }

    const responseId = currentStep.task.responseId || addAssessmentTaskResponseId(dispatch);
    const stepWithResponseId = {
      ...currentStep,
      task: { ...currentStep.task, responseId },
    };

    setIsStarting(true);
    trainingInstanceService
      .startStep(currentCourse.id, currentStep.assignorId.id, currentStep.assigneeId.id, stepWithResponseId)
      .then(() => {
        updateCurrentStepInCourse(dispatch, { newStatus: 'in_progress' });
        return getAssessmentById(currentStep.task.assessmentId, responseId);
      })
      .then(assessmentData => {
        setAssessment(assessmentData);
        updateViewMode({ type: 'taking', responseId });
      })
      .catch(handleError)
      .finally(() => setIsStarting(false));
  };

  const handleAllQuestionsAnswered = (completedAssessment: AssessmentState) => {
    setAssessment(completedAssessment);
    updateViewMode({ type: 'completed' });
    completeStep();
  };

  useEffect(() => {
    getAssessmentById(currentStep.task.assessmentId, currentStep.task.responseId)
      .then(assessmentData => {
        const initial = deriveInitialViewMode(assessmentData, currentStep.status);
        updateViewMode({ type: initial });
        if (initial === 'completed' && currentStep.status !== 'complete') {
          completeStep();
        }
        setAssessment(assessmentData);
      })
      .catch(handleError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadState.status === 'loading') {
    return <LoadingSection />;
  }

  if (loadState.status === 'error') {
    return <ErrorMessage />;
  }

  const { assessment } = loadState;

  const renderView = () => {
    switch (viewMode.type) {
      case 'notStarted':
        return (
          <NotStartedView
            assessmentName={assessment.name}
            questionCount={assessment.questions.length}
            introduction={assessment.introduction}
            isStarting={isStarting}
            onStart={handleStart}
          />
        );
      case 'inProgress':
        return (
          <InProgressView
            assessmentName={assessment.name}
            questionCount={assessment.questions.length}
            introduction={assessment.introduction}
            progress={countAnsweredQuestions(assessment)}
            onResume={() => updateViewMode({ type: 'taking', responseId: currentStep.task.responseId })}
          />
        );
      case 'taking':
        return (
          <AssessmentTaker
            assessment={assessment}
            responseId={viewMode.responseId}
            onAllQuestionsAnswered={handleAllQuestionsAnswered}
          />
        );
      case 'completed':
        return (
          <CompletedView
            assessmentName={assessment.name}
            questionCount={assessment.questions.length}
            conclusion={assessment.conclusion}
          />
        );
    }
  };

  return (
    <>
      <Section>{renderView()}</Section>
      {isFullyAnswered(assessment) && (
        <TabbedPanel defaultTab="Results">
          <TabbedPanel.Tab name="Results">
            <AssessmentTakerResults assessment={assessment} />
          </TabbedPanel.Tab>
        </TabbedPanel>
      )}
    </>
  );
}
